import { useDebounce } from '@mm/hooks';
import { isContactMessage } from '@mm/state/model/ContactMessageModel';
import {
  ConversationDirection,
  MessageKind,
} from '@mm/state/model/LivechatMessageModel';
import { useActor } from '@xstate/react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useContextSelector } from 'use-context-selector';
import Spinner from '../../../../../common/components/Spinner';
import { ContactContext, EmptySelectedContact } from '../../contactMachine';
import { ConversationContext } from '../../conversationMachine';
import { LiveChatContext } from '../../LiveChatContext';
import {
  ConversationSession,
  getConversationSessionsFromMessages,
  getFormattedDate,
} from '../../shared';
import { ChannelIcon } from '../ChannelIcon';
import { IncomingMessage } from './IncomingMessage/IncomingMessage';
import './Messages.pcss';
import { OutgoingMessage } from './OutgoingMessage/OutgoingMessage';
import { UnsentMessage } from './UnsentMessage/UnsentMessage';

const useComponentStateAndHandlers = () => {
  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );

  const [lcMachineState] = useActor(lcService);

  const { remoteUserId } = lcMachineState.context || {};

  const selectedContact =
    (lcMachineState.context.remoteUserId
      ? (
          lcMachineState.context.contactsRefs[
            lcMachineState.context.remoteUserId
          ]?.state.context as ContactContext
        )?.model
      : undefined) || EmptySelectedContact;

  const conversationMachine = remoteUserId
    ? lcMachineState.context.conversationsRefs[remoteUserId]
    : undefined;

  const { optimisticMessages = [], messages: messagesChangingFast = [] } =
    conversationMachine?.state.context as ConversationContext;

  const messages = useDebounce(messagesChangingFast, 47);

  const selectedContactMachine = remoteUserId
    ? lcMachineState.context.contactsRefs[remoteUserId]
    : undefined;

  const {
    previousSessionId,
    nextSessionId,
    archivedMessages = [],
  } = selectedContactMachine?.state.context as ContactContext;

  const [hoverMessageKey, setHoverMessageKey] = useState<string | undefined>(
    undefined,
  );
  const [messagesBySession, setMessagesBySession] = useState<
    ConversationSession[]
  >([]);

  useEffect(() => {
    // Note: we group conversations to chuncks of archivedMessages that have
    // delays between them of 5+ minutes

    const messagesWithoutNotificatons = archivedMessages.filter(
      m =>
        ![MessageKind.read, MessageKind.delivery, MessageKind.fail].some(
          k => m.kind === k,
        ),
    );

    const conversations = getConversationSessionsFromMessages(
      messagesWithoutNotificatons,
      optimisticMessages,
      false,
      false,
    );
    setMessagesBySession(conversations);
  }, [JSON.stringify(archivedMessages), optimisticMessages.length === 0]);

  const showSpinner = useMemo(
    () =>
      remoteUserId !== undefined &&
      archivedMessages &&
      archivedMessages.length === 0,
    [remoteUserId, archivedMessages],
  );

  const onLoadOlderClick = async () => {
    if (previousSessionId) {
      selectedContactMachine?.send({
        type: 'LC_CONTACT_GET_CONVERSATION_SESSION_MESSAGES',
        payload: {
          sessionId: previousSessionId,
          addingOlder: true,
        },
      });
    }
  };

  const onJumpToRecentClick = () => {
    selectedContactMachine?.send({
      type: 'LC_CONTACT_UPDATE',
      payload: {
        archivedMessages: [],
        selectedSessionId: undefined,
        nextSessionId: undefined,
        previousSessionId: undefined,
      },
    });
  };

  return {
    hoverMessageKey,
    messages,
    messagesBySession,
    onJumpToRecentClick,
    onLoadOlderClick,
    setHoverMessageKey,
    showSpinner,
    nextSessionId,
    previousSessionId,
    remoteUserId,
    selectedContactMachine,
    selectedContact,
  };
};

export const ArchivedMessages = () => {
  const {
    hoverMessageKey,
    messagesBySession,
    onJumpToRecentClick,
    onLoadOlderClick,
    setHoverMessageKey,
    showSpinner,
    nextSessionId,
    previousSessionId,
    remoteUserId,
    selectedContact,
    selectedContactMachine,
    messages,
  } = useComponentStateAndHandlers();

  const sessionRef = useRef<HTMLDivElement>(null);

  const needsToScrollRef =
    selectedContactMachine?.state.context.needsToScrollRef;

  const refreshSelectedContact = useCallback(() => {
    selectedContactMachine?.send({
      type: 'LC_CONTACT_REFRESH',
      payload: remoteUserId || '',
    });
  }, [selectedContactMachine?.id, remoteUserId]);

  useEffect(() => {
    if (needsToScrollRef) {
      sessionRef.current?.scrollIntoView(false);

      selectedContactMachine?.send({
        type: 'LC_CONTACT_UPDATE',
        payload: { needsToScrollRef: false },
      });
    }
  }, [needsToScrollRef]);

  const renderArchivedText = useCallback(
    (isTop: boolean) => {
      return (
        <div
          className={
            isTop
              ? 'live-chat--archived-messages__header'
              : 'live-chat--archived-messages__footer'
          }
          onClick={onJumpToRecentClick}
        >
          <div className="live-chat--archived-messages__main-text">
            Viewing archived conversation session from
          </div>
          <div className="live-chat--archived-messages__time-text">
            {messagesBySession.length > 0 &&
              getFormattedDate(messagesBySession[0].sessionFirstMessageAt)}
          </div>
          <div className="live-chat--archived-messages__jump-text">
            Jump to recent messages
          </div>
        </div>
      );
    },
    [messagesBySession],
  );

  if (!selectedContact) return null;

  const { profilePic, fullName, variables, id: contactId } = selectedContact;
  // Note: Please note that the order of elements is reversed via CSS
  return (
    <div className="live-chat--archived-messages-container">
      {renderArchivedText(true)}
      <div className="live-chat--archived-messages">
        <div ref={sessionRef} />
        {previousSessionId && (
          <div className="live-chat--archived-messages__load--spacer">
            <div
              className="live-chat--archived-messages__load-more"
              onClick={onLoadOlderClick}
            >
              Load older messages...
            </div>
          </div>
        )}
        <Spinner
          show={showSpinner}
          size={50}
          position="relative"
          backgroundColor="transparent"
        />

        {messagesBySession &&
          messagesBySession.map((c, idx) => (
            <div key={idx} className="live-chat--messages-conversation">
              <div className="live-chat--messages-conversation__time">
                <div className="live-chat--messages-conversation__time-hr" />
                <div className="live-chat--messages-conversation__time-text">
                  <div className="live-chat--messages-conversation__time-icon">
                    <ChannelIcon remoteUserId={remoteUserId} />
                  </div>

                  {getFormattedDate(c.sessionFirstMessageAt)}
                </div>
                <div className="live-chat--messages-conversation__time-hr" />
              </div>

              {c.sessionMessages &&
                c.sessionMessages.map((m, i) => {
                  const messageKey = `${idx}-${i}`;
                  const isFirstMessage = i === c.sessionMessages.length - 1;

                  if (isContactMessage(m)) {
                    return <UnsentMessage message={m} />;
                  }

                  return m.direction === ConversationDirection.incoming ? (
                    <IncomingMessage
                      messages={messages}
                      message={m}
                      isFirstMessage={isFirstMessage}
                      messageKey={messageKey}
                      hoverMessageKey={hoverMessageKey}
                      setHoverMessageKey={setHoverMessageKey}
                      profilePic={profilePic}
                      fullName={fullName}
                      variables={variables}
                      contactId={contactId}
                      refreshSelectedContact={refreshSelectedContact}
                    />
                  ) : (
                    <OutgoingMessage
                      messages={messages}
                      message={m}
                      isFirstMessage={isFirstMessage}
                      messageKey={messageKey}
                      profilePic={profilePic}
                      isFinalMessage={
                        idx === 0 && i === 0 && nextSessionId === undefined
                      }
                    />
                  );
                })}
            </div>
          ))}
        {nextSessionId && (
          <div className="live-chat--archived-messages__load--spacer">
            <div
              className="live-chat--archived-messages__load-more"
              onClick={() =>
                selectedContactMachine?.send({
                  type: 'LC_CONTACT_GET_CONVERSATION_SESSION_MESSAGES',
                  payload: {
                    sessionId: nextSessionId,
                    addingOlder: false,
                  },
                })
              }
            >
              Load newer messages...
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
